import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';

// Helper function to fetch store name by seller ID
export async function getStoreNameBySellerId(sellerId) {
  try {
    const shopsRef = collection(db, 'shops');
    const q = query(shopsRef, where('sellerId', '==', sellerId));
    const shopSnap = await getDocs(q);
    
    if (!shopSnap.empty) {
      const shopDoc = shopSnap.docs[0];
      const shopData = shopDoc.data();
      return shopData.name || shopData.storeName || 'Store'; // Try different field names
    }
    
    return 'Store'; // Fallback if no shop found
  } catch (error) {
    console.error('Error fetching store name:', error);
    return 'Store'; // Fallback on error
  }
}

// Fetch all order messages for a seller
export async function getOrderMessages(sellerId) {
  try {
    const messagesRef = collection(db, 'orderMessages');
    const q = query(
      messagesRef,
      where('receiverId', '==', sellerId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      });
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching order messages:', error);
    throw error;
  }
}

// Fetch messages for a specific order
export async function getOrderMessagesByOrderId(orderId) {
  try {
    const messagesRef = collection(db, 'orderMessages');
    const q = query(
      messagesRef,
      where('orderId', '==', orderId),
      orderBy('timestamp', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      });
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching order messages by order ID:', error);
    throw error;
  }
}

// Get messages for a customer (where customer is sender or receiver)
export async function getCustomerMessages(customerId) {
  try {
    console.log('🔍 getCustomerMessages - Fetching messages for customer:', customerId);
    
    const messagesRef = collection(db, 'orderMessages');
    
    // Query 1: Messages where customer is sender
    const senderQuery = query(
      messagesRef,
      where('senderId', '==', customerId),
      orderBy('timestamp', 'desc')
    );
    
    // Query 2: Messages where customer is receiver
    const receiverQuery = query(
      messagesRef,
      where('receiverId', '==', customerId),
      orderBy('timestamp', 'desc')
    );
    
    console.log('📤 getCustomerMessages - Querying sender messages...');
    console.log('📥 getCustomerMessages - Querying receiver messages...');
    
    const [senderSnapshot, receiverSnapshot] = await Promise.all([
      getDocs(senderQuery),
      getDocs(receiverQuery)
    ]);
    
    console.log('📤 getCustomerMessages - Sender messages found:', senderSnapshot.docs.length);
    console.log('📥 getCustomerMessages - Receiver messages found:', receiverSnapshot.docs.length);
    
    const messages = [];
    
    // Process sender messages
    senderSnapshot.forEach((doc) => {
      const messageData = {
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      };
      console.log('📤 getCustomerMessages - Sender message:', messageData);
      messages.push(messageData);
    });
    
    // Process receiver messages
    receiverSnapshot.forEach((doc) => {
      const messageData = {
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      };
      console.log('📥 getCustomerMessages - Receiver message:', messageData);
      messages.push(messageData);
    });
    
    // Remove duplicates and sort by timestamp
    const uniqueMessages = messages.filter((message, index, self) => 
      index === self.findIndex(m => m.id === message.id)
    );
    
    // Filter out test messages
    const filteredMessages = uniqueMessages.filter(message => {
      // Exclude messages with test order IDs
      if (message.orderId && (
        message.orderId.includes('test') || 
        message.orderId.includes('TEST') ||
        message.orderId === 'ROSRL6' ||
        message.orderId.startsWith('test-') ||
        message.orderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') // Test customer ID
      )) {
        console.log('🚫 getCustomerMessages - Filtering out test message:', message.orderId);
        return false;
      }
      
      // Exclude test messages with specific content
      if (message.message && (
        message.message.includes('Hello! This is a test message') ||
        message.message.includes('test message') ||
        message.message.includes('Test message') ||
        message.message.includes('TEST MESSAGE') ||
        message.message.toLowerCase().includes('test')
      )) {
        console.log('🚫 getCustomerMessages - Filtering out test message content:', message.message);
        return false;
      }
      
      // Exclude messages from test users
      if (message.senderId && (
        message.senderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') ||
        message.receiverId && message.receiverId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1')
      )) {
        console.log('🚫 getCustomerMessages - Filtering out test user message:', message.senderId, message.receiverId);
        return false;
      }
      
      return true;
    });
    
    console.log('✅ getCustomerMessages - Final filtered messages:', filteredMessages.length);
    
    return filteredMessages.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error fetching customer messages:', error);
    return [];
  }
}


// Create a proper customer-seller message from existing seller messages
export async function createCustomerSellerMessage(originalMessage, customerId) {
  try {
    const messagesRef = collection(db, 'orderMessages');
    
    // Create a new message where the customer is the receiver
    const newMessage = {
      orderId: originalMessage.orderId,
      senderId: originalMessage.senderId, // Keep the original sender (seller)
      receiverId: customerId, // Set customer as receiver
      senderName: originalMessage.senderName,
      receiverName: 'Customer', // We'll update this with actual customer name
      senderType: originalMessage.senderType || 'seller',
      receiverType: 'customer',
      message: originalMessage.message,
      timestamp: originalMessage.timestamp,
      isSelfMessage: false // This is not a self message
    };
    
    const docRef = await addDoc(messagesRef, newMessage);
    console.log('✅ Created customer-seller message:', docRef.id);
    return { id: docRef.id, ...newMessage };
  } catch (error) {
    console.error('Error creating customer-seller message:', error);
    throw error;
  }
}

// Send a new message
export async function sendOrderMessage(messageData) {
  try {
    const messagesRef = collection(db, 'orderMessages');
    const newMessage = {
      ...messageData,
      timestamp: serverTimestamp(),
      isSelfMessage: messageData.senderId === messageData.receiverId // Only true if sender and receiver are the same
    };
    
    const docRef = await addDoc(messagesRef, newMessage);
    return { id: docRef.id, ...newMessage };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}

// Real-time listener for order messages
export function subscribeToOrderMessages(sellerId, callback) {
  try {
    console.log('Setting up subscription for sellerId:', sellerId);
    const messagesRef = collection(db, 'orderMessages');
    const q = query(
      messagesRef,
      where('receiverId', '==', sellerId),
      orderBy('timestamp', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Snapshot received, size:', snapshot.size);
      const messages = [];
      snapshot.forEach((doc) => {
        console.log('Message doc:', doc.id, doc.data());
        messages.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        });
      });
      console.log('Processed messages:', messages);
      callback(messages);
    }, (error) => {
      console.error('Error in real-time messages subscription:', error);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time messages subscription:', error);
    return () => {};
  }
}

// Real-time listener for messages in a specific order
export function subscribeToOrderMessagesByOrderId(orderId, callback) {
  try {
    const messagesRef = collection(db, 'orderMessages');
    const q = query(
      messagesRef,
      where('orderId', '==', orderId),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = [];
      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        });
      });
      callback(messages);
    }, (error) => {
      console.error('Error in real-time order messages subscription:', error);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time order messages subscription:', error);
    return () => {};
  }
}

// Helper function to get order status by order ID
async function getOrderStatusById(orderId, sellerId) {
  try {
    // First try to get merchant order directly
    const merchantOrderRef = doc(db, 'merchant_orders', orderId);
    const merchantOrderSnap = await getDoc(merchantOrderRef);
    
    if (merchantOrderSnap.exists()) {
      const merchantOrder = merchantOrderSnap.data();
      if (merchantOrder.sellerId === sellerId) {
        // Get order items for status determination
        const orderItemsQuery = query(
          collection(db, 'order_items'),
          where('merchantOrderId', '==', orderId)
        );
        const orderItemsSnap = await getDocs(orderItemsQuery);
        
        if (!orderItemsSnap.empty) {
          const orderItems = orderItemsSnap.docs.map(doc => doc.data());
          // Use the same status logic as SellerMessages
          const serviceGroups = {};
          orderItems.forEach(item => {
            const serviceDate = item.serviceDate || 'unknown';
            if (!serviceGroups[serviceDate]) {
              serviceGroups[serviceDate] = [];
            }
            serviceGroups[serviceDate].push(item);
          });
          
          const firstGroupItems = Object.values(serviceGroups)[0] || [];
          if (firstGroupItems.length > 0) {
            // Import getGroupStatus from the same file
            const { getGroupStatus } = await import('../utils/multiVendorOrderApi');
            return getGroupStatus(firstGroupItems);
          }
        }
      }
    }
    
    // If direct fetch fails, try parent order ID lookup
    const merchantOrdersQuery = query(
      collection(db, 'merchant_orders'),
      where('parentOrderId', '==', orderId),
      where('sellerId', '==', sellerId)
    );
    const merchantOrdersSnap = await getDocs(merchantOrdersQuery);
    
    if (!merchantOrdersSnap.empty) {
      const merchantOrderDoc = merchantOrdersSnap.docs[0];
      const merchantOrder = merchantOrderDoc.data();
      
      // Get order items for status determination
      const orderItemsQuery = query(
        collection(db, 'order_items'),
        where('merchantOrderId', '==', merchantOrderDoc.id)
      );
      const orderItemsSnap = await getDocs(orderItemsQuery);
      
      if (!orderItemsSnap.empty) {
        const orderItems = orderItemsSnap.docs.map(doc => doc.data());
        const serviceGroups = {};
        orderItems.forEach(item => {
          const serviceDate = item.serviceDate || 'unknown';
          if (!serviceGroups[serviceDate]) {
            serviceGroups[serviceDate] = [];
          }
          serviceGroups[serviceDate].push(item);
        });
        
        const firstGroupItems = Object.values(serviceGroups)[0] || [];
        if (firstGroupItems.length > 0) {
          const { getGroupStatus } = await import('../utils/multiVendorOrderApi');
          return getGroupStatus(firstGroupItems);
        }
      }
    }
    
    return 'PLACED'; // Default status
  } catch (error) {
    console.error('Error fetching order status:', error);
    return 'PLACED'; // Default status on error
  }
}

// Get unique orders from messages for a seller
export async function getUniqueOrdersFromMessages(messages, sellerId) {
  const orderMap = new Map();
  
  // Filter out test messages first
  const filteredMessages = messages.filter(message => {
    // Exclude messages with test order IDs
    if (message.orderId && (
      message.orderId.includes('test') || 
      message.orderId.includes('TEST') ||
      message.orderId === 'ROSRL6' ||
      message.orderId.startsWith('test-') ||
      message.orderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') // Test customer ID
    )) {
      console.log('🚫 getUniqueOrdersFromMessages - Filtering out test message:', message.orderId);
      return false;
    }
    
    // Exclude test messages with specific content
    if (message.message && (
      message.message.includes('Hello! This is a test message') ||
      message.message.includes('test message') ||
      message.message.includes('Test message') ||
      message.message.includes('TEST MESSAGE') ||
      message.message.toLowerCase().includes('test')
    )) {
      console.log('🚫 getUniqueOrdersFromMessages - Filtering out test message content:', message.message);
      return false;
    }
    
    // Exclude messages from test users
    if (message.senderId && (
      message.senderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') ||
      message.receiverId && message.receiverId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1')
    )) {
      console.log('🚫 getUniqueOrdersFromMessages - Filtering out test user message:', message.senderId, message.receiverId);
      return false;
    }
    
    return true;
  });
  
  // Collect unique order IDs first
  const uniqueOrderIds = new Set();
  filteredMessages.forEach(message => {
    uniqueOrderIds.add(message.orderId);
  });
  
  // Fetch status for each unique order
  const statusMap = new Map();
  for (const orderId of uniqueOrderIds) {
    const status = await getOrderStatusById(orderId, sellerId);
    statusMap.set(orderId, status);
  }
  
  filteredMessages.forEach(message => {
    if (!orderMap.has(message.orderId)) {
      const status = statusMap.get(message.orderId) || 'PLACED';
      orderMap.set(message.orderId, {
        orderId: message.orderId,
        lastMessage: message.message,
        lastTimestamp: message.timestamp,
        customerName: message.senderName,
        customerId: message.senderId,
        customerType: message.senderType,
        status: status,
        unread: false // You can implement unread logic based on your requirements
      });
    } else {
      const existing = orderMap.get(message.orderId);
      if (message.timestamp > existing.lastTimestamp) {
        existing.lastMessage = message.message;
        existing.lastTimestamp = message.timestamp;
        existing.customerName = message.senderName;
        existing.customerId = message.senderId;
        existing.customerType = message.senderType;
        // Update status if needed
        const status = statusMap.get(message.orderId) || 'PLACED';
        existing.status = status;
      }
    }
  });
  
  return Array.from(orderMap.values()).sort((a, b) => b.lastTimestamp - a.lastTimestamp);
}

// Helper function to get consistent order ID for messaging
export function getMessagingOrderId(orderData) {
  // Priority: merchantOrderId > parentOrderId > orderId
  return orderData.merchantOrderId || orderData.parentOrderId || orderData.orderId || orderData.id;
}

// Get unique orders from messages for a customer
export async function getUniqueOrdersFromCustomerMessages(messages, customerId) {
  console.log('🔍 getUniqueOrdersFromCustomerMessages - Processing messages:', messages.length, 'for customer:', customerId);
  
  // Filter out test messages first
  const filteredMessages = messages.filter(message => {
    // Exclude messages with test order IDs
    if (message.orderId && (
      message.orderId.includes('test') || 
      message.orderId.includes('TEST') ||
      message.orderId === 'ROSRL6' ||
      message.orderId.startsWith('test-') ||
      message.orderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') // Test customer ID
    )) {
      console.log('🚫 getUniqueOrdersFromCustomerMessages - Filtering out test message:', message.orderId);
      return false;
    }
    
    // Exclude test messages with specific content
    if (message.message && (
      message.message.includes('Hello! This is a test message') ||
      message.message.includes('test message') ||
      message.message.includes('Test message') ||
      message.message.includes('TEST MESSAGE') ||
      message.message.toLowerCase().includes('test')
    )) {
      console.log('🚫 getUniqueOrdersFromCustomerMessages - Filtering out test message content:', message.message);
      return false;
    }
    
    // Exclude messages from test users
    if (message.senderId && (
      message.senderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') ||
      message.receiverId && message.receiverId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1')
    )) {
      console.log('🚫 getUniqueOrdersFromCustomerMessages - Filtering out test user message:', message.senderId, message.receiverId);
      return false;
    }
    
    return true;
  });
  
  console.log('🔍 getUniqueOrdersFromCustomerMessages - Filtered messages:', filteredMessages.length);
  
  const orderMap = new Map();
  
  // Process messages and collect unique seller IDs
  const sellerIds = new Set();
  filteredMessages.forEach(message => {
    const sellerId = message.senderId === customerId ? message.receiverId : message.senderId;
    if (sellerId) {
      sellerIds.add(sellerId);
    }
  });
  
  // Fetch store names for all unique seller IDs
  const storeNamesMap = new Map();
  for (const sellerId of sellerIds) {
    const storeName = await getStoreNameBySellerId(sellerId);
    storeNamesMap.set(sellerId, storeName);
  }
  
  filteredMessages.forEach(message => {
    console.log('📨 Processing message:', message);
    
    if (!orderMap.has(message.orderId)) {
      // Determine seller info based on who the customer is talking to
      const sellerId = message.senderId === customerId ? message.receiverId : message.senderId;
      const storeName = storeNamesMap.get(sellerId) || 'Store';
      
      const chatData = {
        orderId: message.orderId,
        lastMessage: message.message,
        lastTimestamp: message.timestamp,
        sellerName: storeName, // Now using store name instead of seller name
        sellerId: sellerId,
        unread: false // You can implement unread logic based on your requirements
      };
      
      console.log('🆕 Creating new chat entry:', chatData);
      orderMap.set(message.orderId, chatData);
    } else {
      const existing = orderMap.get(message.orderId);
      if (message.timestamp > existing.lastTimestamp) {
        existing.lastMessage = message.message;
        existing.lastTimestamp = message.timestamp;
        // Update seller info if needed
        const sellerId = message.senderId === customerId ? message.receiverId : message.senderId;
        const storeName = storeNamesMap.get(sellerId) || 'Store';
        existing.sellerName = storeName; // Now using store name
        existing.sellerId = sellerId;
        console.log('🔄 Updated existing chat entry:', existing);
      }
    }
  });
  
  const result = Array.from(orderMap.values()).sort((a, b) => b.lastTimestamp - a.lastTimestamp);
  console.log('✅ getUniqueOrdersFromCustomerMessages - Final result:', result);
  
  return result;
}
